import React from "react";
import { useSelector } from "react-redux";

// material-ui
import makeStyles from "@material-ui/styles/makeStyles";
import Paper from "@material-ui/core/Paper";

import { PageHeader, SimpleForm, TextInput, AutoCompleteArrayInput, SelectArrayInput, showNotification, required, regex, translate } from "jazasoft";

// helper
import { inputOptions, isEmpty, listFromCsv } from "../../utils/helpers";
import { REGEX_USERNAME, REGEX_EMAIL } from "../../utils/regex";
import useUser from "./useUser";
import { Role } from "../../utils/types";
import getValidation from "../../utils/getValidation";
import { UsersFields } from "../../utils/entityFields";
import { useSetting } from "../../hooks";
import SettingKeys from "../../utils/SettingKeys";

const useStyles = makeStyles(theme => ({
  container: {
    margin: theme.spacing(3),
    padding: theme.spacing(3)
  }
}));

const UserEdit = props => {
  const { translate } = props;
  const { roles, appUser, iamUser, app, choicesCustomer, choicesWarehouse, unitList, dispatch, onUpdate } = useUser(props);

  const fields = useSetting(SettingKeys.ENTITY_FIELDS, "users");
  const validationRule = useSetting(SettingKeys.ENTITY_FIELD_VALIDATION, "users");

  const formRoleIds = useSelector(state => state.form && state.form["record-form"]?.values?.roleIds);

  const roleMerchant = !isEmpty(roles) ? roles.find(r => r.roleId === Role.MERCHANT) : {};
  const roleWarehouse = !isEmpty(roles) ? roles.find(r => r.roleId === Role.WAREHOUSE) : {};
  const roleAdmin = !isEmpty(roles) ? roles.find(r => r?.roleId === Role.ADMIN_USER) : {};
  const fabricStroeManagment = !isEmpty(roles) ? roles.find(r => r.roleId === Role.FABRIC_STORE_MANAGEMENT) : {};
  const fabricQualityManagment = !isEmpty(roles) ? roles.find(r => r.roleId === Role.FABRIC_QUALITY_MANAGEMENT) : {};

  const initialValues = {
    ...appUser,
    ...iamUser,
    roleIds: iamUser?.roleList?.filter(r => r.appId === app?.id).map(r => r.role?.id) || [],
    unitIds: listFromCsv(appUser.unitIds),
    customerIds: (iamUser && iamUser.permissionList?.filter(p => p.app?.id === app?.id && p.key === "customerId").map(p => p.id)) || [],
    warehouseIds: (iamUser && iamUser.permissionList?.filter(p => p.app?.id === app?.id && p.key === "warehouseId").map(p => p.id)) || []
  };

  const onSubmit = values => {
    if (isEmpty(values.roleIds)) {
      dispatch(showNotification("resources.users.error.roles_required", { type: "danger" }));
      return;
    } else if (values.roleIds.includes(roleAdmin?.id) && values.roleIds.length > 1) {
      dispatch(showNotification("resources.users.error.not_valid_selection", { type: "danger" }));
      return;
    } else if (values.roleIds.includes(roleMerchant?.id) && isEmpty(values.customerIds)) {
      dispatch(showNotification(translate("resources.users.error.customer_required", { role: "Merchant" }), { type: "danger" }));
      return;
    } else if (values.roleIds.includes(roleWarehouse?.id) && isEmpty(values.warehouseIds)) {
      dispatch(showNotification(translate("resources.users.error.warehouse_required", { role: "Warehouse" }), { type: "danger" }));
      return;
    }
    onUpdate(values);
  };

  const classes = useStyles();

  return (
    <div>
      <PageHeader title={translate("resources.users.page.edit")} />

      <Paper className={classes.container}>
        <SimpleForm record={initialValues} onSubmit={onSubmit}>
          {unitList?.length > 1 && <SelectArrayInput validate={[required()]} source="unitIds" choices={unitList} {...inputOptions(3)} />}
          {fields.includes(UsersFields.FULL_NAME) && (
            <TextInput source="fullName" validate={getValidation(validationRule[UsersFields.FULL_NAME])} {...inputOptions(3)} />
          )}
          {fields.includes(UsersFields.USER_NAME) && (
            <TextInput
              source="username"
              validate={
                getValidation(validationRule[UsersFields.USER_NAME])[
                  regex(
                    REGEX_USERNAME,
                    "Username should consists of Alphabets, numbers and (hyphen, underscore, dot) special characters only and starting with Alphabet"
                  )
                ]
              }
              {...inputOptions(3)}
            />
          )}
          {fields.includes(UsersFields.EMAIL) && (
            <TextInput
              source="email"
              validate={getValidation(validationRule[UsersFields.EMAIL])[regex(REGEX_EMAIL, "Invalid Email")]}
              {...inputOptions(3)}
            />
          )}
          {fields.includes(UsersFields.MOBILE) && (
            <TextInput source="mobile" validate={getValidation(validationRule[UsersFields.ROLES])} {...inputOptions(3)} />
          )}
          {fields.includes(UsersFields.ROLES) && (
            <SelectArrayInput label="Roles" source="roleIds" validate={[required()]} choices={roles} {...inputOptions(3)} />
          )}
          {!isEmpty(roleMerchant) && formRoleIds?.includes(roleMerchant.id) && fields.includes(UsersFields.CUSTOMER) && (
            <AutoCompleteArrayInput
              label="Customers"
              allowEmpty={true}
              source="customerIds"
              choices={choicesCustomer}
              validate={getValidation(validationRule[UsersFields.CUSTOMER])}
              {...inputOptions(6)}
            />
          )}
          {!isEmpty(choicesWarehouse) &&
            (formRoleIds?.includes(roleWarehouse?.id) ||
              formRoleIds?.includes(fabricQualityManagment?.id) ||
              formRoleIds?.includes(fabricStroeManagment?.id)) &&
            fields.includes(UsersFields.WAREHOUSES) && (
              <SelectArrayInput
                label="Warehouses"
                allowEmpty={true}
                source="warehouseIds"
                choices={choicesWarehouse}
                validate={getValidation(validationRule[UsersFields.CUSTOMER])}
                {...inputOptions(6)}
              />
            )}
        </SimpleForm>
      </Paper>
    </div>
  );
};

export default translate(UserEdit);
